import { computed, type Ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDevice } from '@/composables/useDevice';
import { useSearchStore } from '@/stores/searchStore';
import {
  useTrackersListStatusStore,
  type TrackerListStatusContext,
} from '@/stores/useTrackersListStatusStore';
import { filterDeviceIds } from '@/utils/device-filter';

type UseTrackerListFilterOptions = {
  /** Keeps deviceStore.resultDeviceIds in sync for legacy consumers (All Trackers page). */
  syncToDeviceStore?: boolean;
  context?: TrackerListStatusContext;
};

export function useTrackerListFilter(
  baseDeviceIds?: Ref<number[] | undefined>,
  options: UseTrackerListFilterOptions = {},
) {
  const context =
    options.context ?? (baseDeviceIds ? 'groupDetail' : 'trackers');
  const searchStore = useSearchStore();
  const statusStore = useTrackersListStatusStore();
  const deviceStore = useDevice();
  const { devices, filter } = storeToRefs(deviceStore);

  const allDeviceIds = computed(() => Array.from(devices.value.keys()));

  const scopeIds = computed(() => {
    const scoped = baseDeviceIds?.value;
    return scoped ?? allDeviceIds.value;
  });

  const filteredDeviceIds = computed(() =>
    filterDeviceIds(scopeIds.value, devices.value, {
      search: searchStore.normalizedQuery,
      filter: filter.value,
      selectedStatuses: statusStore.getSelectedStatuses(context),
    }),
  );

  const hasBaseDevices = computed(() => scopeIds.value.length > 0);
  const hasFilteredResults = computed(() => filteredDeviceIds.value.length > 0);

  if (options.syncToDeviceStore) {
    watch(
      filteredDeviceIds,
      (ids) => {
        deviceStore.resultDeviceIds = ids;
      },
      { immediate: true },
    );
  }

  return {
    filteredDeviceIds,
    hasBaseDevices,
    hasFilteredResults,
  };
}

/**
 * Non-composable helper for Pinia stores and download handlers that need the same filter inputs.
 */
export function getTrackerListFilterParams(
  context: TrackerListStatusContext = 'trackers',
) {
  const searchStore = useSearchStore();
  const statusStore = useTrackersListStatusStore();
  const deviceStore = useDevice();

  return {
    search: searchStore.normalizedQuery,
    filter: deviceStore.filter,
    selectedStatuses: statusStore.getSelectedStatuses(context),
    devices: deviceStore.devices,
  };
}
